import React, { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Users, AlertTriangle, Search, Plus, Check, Loader2, Clock, Trash2, Copy,
  CalendarRange, ArrowLeft, CheckSquare, Square, MapPin, Ban, Phone, MessageCircle,
  CalendarClock, CheckCircle2, Pencil, X, Shuffle,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { frenchDate, frenchDateShort, frenchDayChip, frenchDateFull } from "@/lib/shariti";
import { dispoForDate, dispoBadge, creneauOutsideDispo } from "@/lib/dispo";

// Lien WhatsApp : normalise un numéro FR (0X… → 33X…) vers wa.me. Renvoie null si vide.
const waHref = (phone) => {
  if (!phone) return null;
  let d = String(phone).replace(/[^\d]/g, "");
  if (d.startsWith("0")) d = "33" + d.slice(1);
  return d ? `https://wa.me/${d}` : null;
};

// Convertit une disponibilité DATÉE (saisie par le responsable : {mode, debut, fin})
// vers la forme d'« entrée » attendue par dispoBadge / creneauOutsideDispo.
function datedDispoEntry(dj) {
  if (!dj || !dj.mode) return null;
  if (dj.mode === "indispo") return { mode: "indispo" };
  if (dj.debut && dj.fin) return { mode: "creneau", debut: dj.debut, fin: dj.fin };
  return { mode: "journee" };
}

// Rappel COMPACT (écrit en petit) de la disponibilité déclarée d'un collaborateur
// pour le JOUR sélectionné. Si un créneau est fourni, signale s'il déborde de la
// disponibilité. Purement visuel — n'impacte aucun calcul.
// `entry` (prioritaire) = dispo DATÉE déjà résolue ; sinon on retombe sur `dispo`+`date`.
function DispoMini({ dispo, date, entry, creneau, testId }) {
  const resolved = entry !== undefined ? entry : dispoForDate(dispo, date);
  const { tone, label } = dispoBadge(resolved);
  const out = creneau ? creneauOutsideDispo(resolved, creneau.debut, creneau.fin) : false;
  const cls = { ok: "text-[#0F5A2F]", partial: "text-[#1D4ED8]", off: "text-[#9B2226]", none: "text-muted-ink" }[tone];
  const Icon = tone === "off" ? Ban : tone === "none" ? CalendarClock : tone === "ok" ? CheckCircle2 : Clock;
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 text-[10px] font-semibold ${cls}`}
      title={`Disponibilité déclarée : ${label}`} data-testid={testId}>
      <Icon className="h-3 w-3 shrink-0" /> {label}
      {out && (
        <span className="inline-flex items-center gap-0.5 rounded bg-[rgba(214,69,69,0.12)] px-1 py-0.5 text-[9px] font-bold text-[#9B2226]"
          title="Le créneau choisi est hors de sa disponibilité déclarée">
          <AlertTriangle className="h-2.5 w-2.5" /> hors dispo
        </span>
      )}
    </span>
  );
}

// Créneaux rapides proposés à l'affectation (repris tels quels des Sites Privés).
const TEAM_SLOTS = [
  { label: "10h – 14h", cd: "10:00", cf: "14:00" },
  { label: "10h – 15h", cd: "10:00", cf: "15:00" },
  { label: "10h – 20h", cd: "10:00", cf: "20:00" },
  { label: "15h – 20h", cd: "15:00", cf: "20:00" },
  { label: "16h – 20h", cd: "16:00", cf: "20:00" },
];

/**
 * Panneau « Équipe du jour » générique (pointage terrain).
 * target = { kind: "prive"|"pap", id, date, name, address }
 *  - prive : id = booking (journée-spot), contrôle GPS = point du spot.
 *  - pap   : id = cellule PAP (ville/jour), contrôle GPS = ZONE tracée.
 * weekDays = liste des dates ISO de la semaine affichée (pour la reprise multi-jours).
 */
export function TeamSheet({ target, weekDays = [], onClose, onChanged, canEdit = true }) {
  const open = !!target;
  const kind = target?.kind || "prive";
  const isPap = kind === "pap";
  const { user } = useAuth();
  const isAdmin = (user?.role === "admin");   // l'admin a la MAIN TOTALE (override audit)

  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGeo, setHasGeo] = useState(true);
  const [spotId, setSpotId] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [q, setQ] = useState("");
  const [rds, setRds] = useState([]);
  // Mémorise le dernier créneau choisi (proposé par défaut à la prochaine ouverture).
  const savedSlot = (() => {
    try { return JSON.parse(localStorage.getItem("shariti_team_slot") || "null"); } catch { return null; }
  })();
  const savedIdx = (savedSlot && Number.isInteger(savedSlot.slotIdx) && savedSlot.slotIdx >= 0 && savedSlot.slotIdx < TEAM_SLOTS.length) ? savedSlot.slotIdx : 0;
  const [slotIdx, setSlotIdx] = useState(savedIdx);
  const [customMode, setCustomMode] = useState(!!(savedSlot && savedSlot.customMode));
  const [cd, setCd] = useState((savedSlot && savedSlot.cd) || "10:00");
  const [cf, setCf] = useState((savedSlot && savedSlot.cf) || "20:00");
  const [busyId, setBusyId] = useState(null);

  // Édition INLINE du créneau d'un membre déjà affecté (crayon) — évite de le supprimer
  // puis de le rajouter juste pour ajuster ses heures.
  const [editId, setEditId] = useState(null);   // affectation_id en cours d'édition
  const [eDeb, setEDeb] = useState("10:00");
  const [eFin, setEFin] = useState("20:00");
  const [savingEdit, setSavingEdit] = useState(false);
  const startEdit = (m) => { setEditId(m.affectation_id); setEDeb(m.creneau_debut || "10:00"); setEFin(m.creneau_fin || "20:00"); };
  const cancelEdit = () => { setEditId(null); setSavingEdit(false); };
  const saveEdit = async (m) => {
    if (!eDeb || !eFin || eDeb >= eFin) { toast.error("Heures invalides : le début doit précéder la fin."); return; }
    setSavingEdit(true);
    try {
      await api.put(`/pointage/affectations/${m.affectation_id}`, { creneau_debut: eDeb, creneau_fin: eFin });
      toast.success(`Créneau modifié · ${m.rd_name} · ${eDeb}–${eFin}`);
      setEditId(null);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Modification impossible");
    } finally { setSavingEdit(false); }
  };

  // Annulation TRACÉE d'un pointage (quand la suppression physique est bloquée par l'audit).
  const [annulerTarget, setAnnulerTarget] = useState(null);   // membre concerné
  const [annulerMotif, setAnnulerMotif] = useState("");
  const [annulerBusy, setAnnulerBusy] = useState(false);

  // Reprise d'équipe (aperçu multi-jours)
  const [reuseOpen, setReuseOpen] = useState(false);
  const [reuseLoading, setReuseLoading] = useState(false);
  const [reuse, setReuse] = useState(null);            // { source_date, members, targets, message }
  const [reuseSel, setReuseSel] = useState(new Set());
  const [reuseBusy, setReuseBusy] = useState(false);
  // Ajouts EN PLUS de l'équipe reprise (chacun avec son créneau) — appliqués aux jours cochés.
  const [extraMembers, setExtraMembers] = useState([]);
  // Équipe reprise ÉDITABLE : chaque membre peut être décoché (exclu) et voir son créneau ajusté.
  //   [{ rd_id, rd_name, code_rd, creneau_debut, creneau_fin, indisponible, included }]
  const [reuseTeam, setReuseTeam] = useState([]);

  // Persiste le dernier créneau choisi (proposé par défaut à la prochaine ouverture du panneau).
  useEffect(() => {
    try { localStorage.setItem("shariti_team_slot", JSON.stringify({ slotIdx, customMode, cd, cf })); } catch (_) {}
  }, [slotIdx, customMode, cd, cf]);

  const load = useCallback(async () => {
    if (!target) return;
    setLoading(true);
    try {
      const url = isPap
        ? `/pointage/affectations/by-cell/${target.id}`
        : `/pointage/affectations/by-booking/${target.id}`;
      const { data } = await api.get(url);
      setTeam(data.team || []);
      setHasGeo(isPap ? data.zone_ok !== false : data.spot_has_gps !== false);
      setSpotId(isPap ? null : (data.spot_id || null));
    } catch { setTeam([]); }
    finally { setLoading(false); }
  }, [target, isPap]);

  // 1 clic : renseigne automatiquement le GPS du spot (géocodage de l'adresse)
  // pour débloquer le pointage — sans quitter le planning ni toucher aux heures.
  const autoGeocode = async () => {
    if (!spotId || geocoding) return;
    setGeocoding(true);
    try {
      const { data } = await api.post(`/spots/${spotId}/geocode`);
      if (data?.gps?.lat != null) {
        toast.success("Coordonnées GPS renseignées — pointage géolocalisé débloqué.");
        setHasGeo(true);
        await load();
        onChanged && onChanged();
      } else {
        toast.info("Aucune coordonnée trouvée pour ce spot.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Géocodage impossible — renseignez la position manuellement.");
    } finally { setGeocoding(false); }
  };

  // Remplissage MANUEL de l'équipe depuis les DISPONIBILITÉS datées déclarées par le
  // responsable (Sites Privés uniquement). Utile si l'emplacement a été posé avant la
  // saisie des dispos, ou pour relancer après mise à jour. Non destructif (dédup backend).
  const [fillingDispos, setFillingDispos] = useState(false);
  const fillFromDispos = async () => {
    if (isPap || !target) return;
    setFillingDispos(true);
    try {
      const { data } = await api.post(`/planning/booking/${target.id}/autofill-dispos`);
      const n = data?.created_count || 0;
      if (n > 0) toast.success(`${n} collaborateur(s) ajouté(s) depuis les disponibilités`);
      else toast.info("Aucune disponibilité à ajouter (déjà placés ou aucune dispo déclarée ce jour).");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remplissage impossible.");
    } finally { setFillingDispos(false); }
  };

  // Re-dispatch MANUEL de la JOURNÉE : recalcule les placements automatiques issus des
  // disponibilités pour CONCENTRER l'équipe (3 max par spot : on remplit un spot avant d'en
  // ouvrir un autre). Les placements manuels sont conservés. Jours à venir uniquement.
  const [redispatching, setRedispatching] = useState(false);
  const redispatchDay = async () => {
    if (isPap || !target) return;
    setRedispatching(true);
    try {
      const { data } = await api.post(`/pointage/affectations/redispatch`, { booking_id: target.id });
      toast.success(`Journée re-dispatchée · ${data?.placed ?? 0} collaborateur(s) répartis sur ${data?.spots ?? 0} spot(s)`);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-dispatch impossible.");
    } finally { setRedispatching(false); }
  };

  useEffect(() => {
    if (target) { setQ(""); setRds([]); setReuseOpen(false); setReuse(null); setExtraMembers([]); setReuseTeam([]); load(); }
  }, [target, load]);

  useEffect(() => {
    if (!open || !canEdit) return;
    const t = setTimeout(() => {
      const dateParam = target?.date ? `&date=${encodeURIComponent(String(target.date).slice(0, 10))}` : "";
      api.get(`/pointage/rds?actifs_terrain=true${dateParam}${q ? `&q=${encodeURIComponent(q)}` : ""}`)
        .then((r) => setRds(r.data)).catch(() => setRds([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, canEdit, target]);

  const effCd = customMode ? cd : TEAM_SLOTS[slotIdx].cd;
  const effCf = customMode ? cf : TEAM_SLOTS[slotIdx].cf;
  const assignedIds = new Set(team.map((t) => t.rd_id));

  const assign = async (rd) => {
    if (assignedIds.has(rd.id)) return;
    setBusyId(rd.id);
    // PRÉREMPLISSAGE : si le responsable a déclaré une dispo DATÉE avec un créneau précis,
    // on l'applique automatiquement (« disponible de X à Y ») au lieu du créneau générique.
    const dj = datedDispoEntry(rd.dispo_jour);
    const useCd = (dj && dj.mode === "creneau") ? dj.debut : effCd;
    const useCf = (dj && dj.mode === "creneau") ? dj.fin : effCf;
    try {
      const body = { rd_id: rd.id, creneau_debut: useCd, creneau_fin: useCf };
      if (isPap) body.pap_cell_id = target.id; else body.booking_id = target.id;
      const r = await api.post(`/pointage/affectations`, body);
      toast.success(`${rd.name} affecté(e) · ${useCd}–${useCf}`);
      if (r.data.warning) toast.warning(r.data.warning);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Affectation impossible"); }
    finally { setBusyId(null); }
  };

  const remove = async (m) => {
    // Ligne TRACÉE (pointage porteur de données OU annulation d'audit) → on ouvre le
    // dialog dédié : annulation traçable (0 h) pour tous, et — pour l'ADMIN — l'option
    // « Supprimer définitivement » (main totale, ex. déplacer un spot mal attribué).
    const traced = (m.has_pointage_data && m.pointage_id) || m.annule;
    if (traced) {
      setAnnulerMotif("");
      setAnnulerTarget(m);
      return;
    }
    setBusyId(m.affectation_id);
    try {
      await api.delete(`/pointage/affectations/${m.affectation_id}`);
      toast.success("Retiré de l'équipe");
      await load();
      onChanged && onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Retrait impossible";
      if (e?.response?.status === 409) {
        toast.error(detail, {
          description: "Cette ligne est conservée pour l'audit. Ses heures sont déjà neutralisées (0 h).",
          duration: 8000,
        });
      } else {
        toast.error(detail);
      }
    }
    finally { setBusyId(null); }
  };

  // Annulation tracée du pointage : met les heures à 0 (paie/stats) sans supprimer la trace.
  const confirmAnnuler = async () => {
    const m = annulerTarget;
    if (!m?.pointage_id) return;
    const motif = (annulerMotif || "").trim();
    if (!motif) { toast.info("Indiquez un motif d'annulation."); return; }
    setAnnulerBusy(true);
    try {
      await api.post(`/pointage/pointages/${m.pointage_id}/annuler`, { motif });
      toast.success(`Pointage annulé (0 h) · ${m.rd_name}`, {
        description: "Les heures ne sont plus comptées. La ligne reste tracée pour l'audit.",
      });
      setAnnulerTarget(null);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Annulation impossible");
    } finally { setAnnulerBusy(false); }
  };

  // Override ADMIN : suppression PHYSIQUE forcée d'une ligne tracée (pointée ou annulée).
  // Retire l'affectation + son pointage, malgré la règle d'audit. Le geste est journalisé côté serveur.
  const forceDelete = async () => {
    const m = annulerTarget;
    if (!m?.affectation_id) return;
    setAnnulerBusy(true);
    try {
      await api.delete(`/pointage/affectations/${m.affectation_id}`, { params: { force: true } });
      toast.success(`Ligne supprimée définitivement · ${m.rd_name}`, {
        description: "Retrait physique effectué (action tracée dans le journal).",
      });
      setAnnulerTarget(null);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally { setAnnulerBusy(false); }
  };

  // --- Reprise d'équipe : ouvre l'aperçu (multi-jours) ---
  const openReuse = async () => {
    setReuseOpen(true);
    setReuseLoading(true);
    setReuse(null);
    setExtraMembers([]);
    setQ("");
    try {
      const dates = (weekDays && weekDays.length ? weekDays : [target.date]).map((d) => String(d).slice(0, 10));
      const body = { to_dates: dates, preview: true };
      if (isPap) body.to_cell_id = target.id; else body.to_booking_id = target.id;
      const { data } = await api.post(`/pointage/affectations/duplicate`, body);
      setReuse(data);
      // Équipe reprise éditable : cochée par défaut (sauf indisponibles), créneau d'origine modifiable.
      setReuseTeam((data.members || []).map((m) => ({
        rd_id: m.rd_id, rd_name: m.rd_name, code_rd: m.code_rd,
        creneau_debut: m.creneau_debut || "10:00", creneau_fin: m.creneau_fin || "20:00",
        indisponible: !!m.indisponible, included: !m.indisponible,
      })));
      // Sélection par défaut des jours cibles :
      //  • source "after" (repli) → l'équipe vient d'un jour POSTÉRIEUR déjà staffé
      //    (ex. Super U) : on coche UNIQUEMENT le jour cible cliqué (ex. Strasbourg) pour
      //    ne pas re-garnir involontairement toute la semaine.
      //  • sinon (source "before" = semaine précédente) → comportement historique :
      //    tous les jours cibles existants sont cochés.
      const targetDay = String(target.date).slice(0, 10);
      const existingDays = (data.targets || []).filter((t) => t.exists).map((t) => t.date);
      const sel = data.source_direction === "after"
        ? new Set(existingDays.includes(targetDay) ? [targetDay] : existingDays)
        : new Set(existingDays);
      setReuseSel(sel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aperçu impossible");
      setReuseOpen(false);
    } finally { setReuseLoading(false); }
  };

  const toggleDay = (d) => {
    setReuseSel((prev) => {
      const n = new Set(prev);
      n.has(d) ? n.delete(d) : n.add(d);
      return n;
    });
  };

  // Ajout d'une personne EN PLUS de l'équipe reprise, avec le créneau actuellement sélectionné.
  const sourceIds = new Set(reuseTeam.map((m) => m.rd_id));
  const extraIds = new Set(extraMembers.map((e) => e.rd_id));
  const addExtra = (rd) => {
    if (sourceIds.has(rd.id) || extraIds.has(rd.id)) {
      toast.info(`${rd.name} est déjà dans l'équipe.`);
      return;
    }
    const dj = datedDispoEntry(rd.dispo_jour);
    const acd = (dj && dj.mode === "creneau") ? dj.debut : effCd;
    const acf = (dj && dj.mode === "creneau") ? dj.fin : effCf;
    setExtraMembers((prev) => [...prev, {
      rd_id: rd.id, rd_name: rd.name, code: rd.code,
      population: rd.population, creneau_debut: acd, creneau_fin: acf,
    }]);
    setQ("");
    toast.success(`${rd.name} ajouté(e) · ${acd}–${acf}`);
  };
  const removeExtra = (rid) => setExtraMembers((prev) => prev.filter((e) => e.rd_id !== rid));
  const setExtraCreneau = (rid, field, value) => setExtraMembers((prev) => prev.map((e) =>
    (e.rd_id === rid ? { ...e, [field]: value } : e)));

  // Équipe reprise : (dé)cocher un membre, ou ajuster son créneau individuellement.
  const toggleReuseMember = (rid) => setReuseTeam((prev) => prev.map((m) =>
    (m.rd_id === rid && !m.indisponible) ? { ...m, included: !m.included } : m));
  const setReuseMemberCreneau = (rid, field, value) => setReuseTeam((prev) => prev.map((m) =>
    (m.rd_id === rid ? { ...m, [field]: value } : m)));
  const includedCount = reuseTeam.filter((m) => m.included && !m.indisponible).length;

  const availableTargets = (reuse?.targets || []).filter((t) => t.exists);
  const allSelected = availableTargets.length > 0 && availableTargets.every((t) => reuseSel.has(t.date));
  const toggleAll = () => {
    setReuseSel(allSelected ? new Set() : new Set(availableTargets.map((t) => t.date)));
  };

  const confirmReuse = async () => {
    const dates = Array.from(reuseSel);
    if (!dates.length) { toast.info("Sélectionnez au moins un jour."); return; }
    const members = reuseTeam.filter((m) => m.included && !m.indisponible)
      .map((m) => ({ rd_id: m.rd_id, creneau_debut: m.creneau_debut, creneau_fin: m.creneau_fin }));
    if (!members.length && !extraMembers.length) {
      toast.info("Sélectionnez au moins une personne à reprendre.");
      return;
    }
    setReuseBusy(true);
    try {
      const body = { to_dates: dates, members_override: members };
      if (isPap) body.to_cell_id = target.id; else body.to_booking_id = target.id;
      if (extraMembers.length) {
        body.extra_members = extraMembers.map((e) => ({
          rd_id: e.rd_id, creneau_debut: e.creneau_debut, creneau_fin: e.creneau_fin,
        }));
      }
      const { data } = await api.post(`/pointage/affectations/duplicate`, body);
      if (data.created > 0) {
        const src = data.source_date ? ` (équipe du ${frenchDateShort(String(data.source_date).slice(0, 10))})` : "";
        toast.success(`${data.created} affectation(s) reprise(s) sur ${dates.length} jour(s)${src}.`);
      } else {
        toast.info(data.message || "Aucun nouveau collaborateur à reprendre.");
      }
      setReuseOpen(false);
      setExtraMembers([]);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reprise impossible"); }
    finally { setReuseBusy(false); }
  };

  // Sélecteur de créneau (partagé : affectation directe ET ajout pendant la reprise).
  const renderSlotPicker = () => (
    <>
      <div className="flex flex-wrap gap-1.5">
        {TEAM_SLOTS.map((s, i) => (
          <button key={s.label} type="button" onClick={() => { setCustomMode(false); setSlotIdx(i); }}
            data-testid={`team-slot-${i}`}
            className={`rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors ${!customMode && slotIdx === i ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.10)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
            {s.label}
          </button>
        ))}
        <button type="button" onClick={() => setCustomMode(true)} data-testid="team-slot-custom"
          className={`rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors ${customMode ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.10)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
          Personnalisé
        </button>
      </div>
      {customMode && (
        <div className="mt-2 grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Début</label>
            <Input type="time" value={cd} onChange={(e) => setCd(e.target.value)} className="mt-1 h-9" data-testid="team-custom-cd" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Fin</label>
            <Input type="time" value={cf} onChange={(e) => setCf(e.target.value)} className="mt-1 h-9" data-testid="team-custom-cf" />
          </div>
        </div>
      )}
    </>
  );

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-md" data-testid="team-sheet">
        <SheetHeader className="border-b border-[var(--border-soft)] bg-[var(--surface-2)] p-4 text-left">
          <SheetTitle className="flex items-center gap-2 text-base font-extrabold text-ink">
            <Users className="h-5 w-5 text-[var(--shariti-orange)]" /> Équipe du jour
            {isPap && <span className="rounded-full bg-[rgba(238,114,46,0.14)] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-[var(--shariti-orange)]">Porte-à-porte</span>}
            {!canEdit && <span className="rounded-full border border-[var(--border-soft)] bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-ink" data-testid="team-sheet-readonly">Consultation</span>}
          </SheetTitle>
          <div className="mt-0.5 text-sm font-semibold text-ink" data-testid="team-sheet-spot">{target?.name || (isPap ? "Ville" : "Spot")}</div>
          <div className="text-[12px] text-muted-ink">
            {target?.date ? frenchDate(String(target.date).slice(0, 10)) : ""}
            {target?.address ? ` · ${target.address}` : ""}
          </div>
        </SheetHeader>

        {canEdit && !hasGeo && (
          <div className="mx-4 mt-4 rounded-xl border border-[rgba(238,114,46,0.35)] bg-[rgba(238,114,46,0.06)] px-3 py-2.5 text-[12px] leading-snug text-[#8a4b1f]" data-testid="team-sheet-nogps">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {isPap
                  ? "Cette ville n'a pas de zone tracée sur la carte : le pointage géolocalisé sera bloqué. Délimitez la zone terrain pour l'activer."
                  : "Ce spot n'a pas de coordonnées GPS : le pointage géolocalisé sera bloqué. Renseignez la position pour l'activer — vous pouvez tout de même modifier l'équipe et les heures."}
              </span>
            </div>
            {!isPap && spotId && (
              <button
                type="button"
                onClick={autoGeocode}
                disabled={geocoding}
                data-testid="team-sheet-geocode"
                className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-[var(--shariti-orange)] px-3 py-1.5 text-[12px] font-bold text-white transition-colors hover:bg-[#E56622] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]"
              >
                {geocoding
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Localisation en cours…</>
                  : <><MapPin className="h-3.5 w-3.5" /> Renseigner le GPS automatiquement</>}
              </button>
            )}
          </div>
        )}

        {/* ================= APERÇU REPRISE (multi-jours) ================= */}
        {reuseOpen ? (
          <div className="space-y-4 p-4" data-testid="team-reuse-panel">
            <button type="button" onClick={() => setReuseOpen(false)} data-testid="team-reuse-back"
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-muted-ink hover:text-ink">
              <ArrowLeft className="h-3.5 w-3.5" /> Retour
            </button>
            {reuseLoading ? (
              <div className="rounded-xl border border-[var(--border-soft)] px-3 py-6 text-center text-[12px] text-muted-ink">
                <Loader2 className="mx-auto mb-1 h-4 w-4 animate-spin" /> Recherche de l'équipe précédente…
              </div>
            ) : !reuse || !(reuse.members || []).length ? (
              <div className="rounded-xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-6 text-center text-[12px] text-muted-ink" data-testid="team-reuse-empty">
                {reuse?.message || "Aucune équipe précédente trouvée pour cette ligne."}
              </div>
            ) : (
              <>
                <div>
                  <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
                    Équipe à reprendre{reuse.source_date ? ` · ${frenchDateFull(String(reuse.source_date).slice(0, 10))}` : ""}
                  </div>
                  {reuse.source_direction === "after" && (
                    <div className="mb-2 flex items-start gap-1.5 rounded-lg border border-[var(--border-soft)] bg-[rgba(238,114,46,0.08)] px-2.5 py-1.5 text-[11px] leading-snug text-ink"
                      data-testid="team-reuse-after-note">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
                      <span>Aucun jour antérieur n'est planifié sur cette ligne : l'équipe est reprise d'un jour <strong>postérieur</strong>. Seul le jour sélectionné est coché par défaut.</span>
                    </div>
                  )}
                  <div className="space-y-1.5" data-testid="team-reuse-members">
                    {reuseTeam.map((m) => {
                      const off = !m.included || m.indisponible;
                      return (
                      <div key={m.rd_id} data-testid={`team-reuse-member-${m.code_rd || m.rd_id}`}
                        className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm ${m.indisponible ? "border-dashed border-[var(--border-soft)] opacity-60" : m.included ? "border-[var(--border-soft)] bg-white" : "border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] opacity-70"}`}>
                        <button type="button" onClick={() => toggleReuseMember(m.rd_id)} disabled={m.indisponible}
                          data-testid={`team-reuse-toggle-member-${m.code_rd || m.rd_id}`}
                          title={m.indisponible ? "Indisponible ce jour-là" : (m.included ? "Retirer de la reprise" : "Réintégrer à la reprise")}
                          className="shrink-0 text-[var(--shariti-orange)] transition-colors disabled:opacity-40">
                          {m.included && !m.indisponible ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                        </button>
                        <span className="min-w-0 flex-1 break-words font-semibold text-ink">{m.rd_name}
                          {m.indisponible && <span className="ml-1 rounded bg-[rgba(214,69,69,0.12)] px-1 text-[9px] font-bold uppercase text-[#9B2226]">indispo</span>}
                          {!m.included && !m.indisponible && <span className="ml-1 rounded bg-[var(--surface-2)] px-1 text-[9px] font-bold uppercase text-muted-ink">exclu</span>}</span>
                        <span className="flex shrink-0 items-center gap-1">
                          <input type="time" value={m.creneau_debut} disabled={off}
                            onChange={(e) => setReuseMemberCreneau(m.rd_id, "creneau_debut", e.target.value)}
                            data-testid={`team-reuse-cd-${m.code_rd || m.rd_id}`}
                            className="h-7 w-[70px] rounded-md border border-[var(--border-soft)] bg-white px-1 text-[10px] font-semibold disabled:opacity-40" />
                          <span className="text-muted-ink">–</span>
                          <input type="time" value={m.creneau_fin} disabled={off}
                            onChange={(e) => setReuseMemberCreneau(m.rd_id, "creneau_fin", e.target.value)}
                            data-testid={`team-reuse-cf-${m.code_rd || m.rd_id}`}
                            className="h-7 w-[70px] rounded-md border border-[var(--border-soft)] bg-white px-1 text-[10px] font-semibold disabled:opacity-40" />
                        </span>
                      </div>
                      );
                    })}
                    {/* Personnes ajoutées à la volée (en plus de l'équipe reprise) */}
                    {extraMembers.map((e) => (
                      <div key={e.rd_id} data-testid={`team-extra-member-${e.code || e.rd_id}`}
                        className="flex items-center gap-2 rounded-lg border border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.06)] px-2.5 py-1.5 text-sm">
                        <span className="min-w-0 flex-1 break-words font-semibold text-ink">{e.rd_name}
                          <span className="ml-1 rounded bg-[rgba(238,114,46,0.16)] px-1 text-[9px] font-bold uppercase text-[var(--shariti-orange)]">Nouveau</span></span>
                        <span className="flex shrink-0 items-center gap-1">
                          <input type="time" value={e.creneau_debut}
                            onChange={(ev) => setExtraCreneau(e.rd_id, "creneau_debut", ev.target.value)}
                            data-testid={`team-extra-cd-${e.code || e.rd_id}`}
                            className="h-7 w-[70px] rounded-md border border-[var(--border-soft)] bg-white px-1 text-[10px] font-semibold" />
                          <span className="text-muted-ink">–</span>
                          <input type="time" value={e.creneau_fin}
                            onChange={(ev) => setExtraCreneau(e.rd_id, "creneau_fin", ev.target.value)}
                            data-testid={`team-extra-cf-${e.code || e.rd_id}`}
                            className="h-7 w-[70px] rounded-md border border-[var(--border-soft)] bg-white px-1 text-[10px] font-semibold" />
                          <button type="button" onClick={() => removeExtra(e.rd_id)} data-testid={`team-extra-remove-${e.code || e.rd_id}`}
                            title="Retirer cet ajout"
                            className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-[#9B2226] transition-colors hover:bg-[rgba(214,69,69,0.10)]">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Ajouter une personne EN PLUS, avec son créneau */}
                  <div className="mt-2.5 rounded-xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5" data-testid="team-reuse-add-block">
                    <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Ajouter une personne</div>
                    <p className="mb-1.5 text-[11px] leading-snug text-muted-ink">Choisissez le créneau, tapez un nom ou un code, puis <b>cliquez sur la personne</b> pour l'ajouter avec ces horaires (modifiables ensuite). Elle sera reprise sur les jours cochés.</p>
                    <div className="mb-2">{renderSlotPicker()}</div>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
                      <Input placeholder="Rechercher par nom ou code…" value={q} onChange={(e) => setQ(e.target.value)}
                        className="h-9 bg-white pl-8" data-testid="team-reuse-search" />
                    </div>
                    {q.trim() && (
                      <div className="mt-2 max-h-44 space-y-1 overflow-y-auto rounded-lg border border-[var(--border-soft)] bg-white p-1" data-testid="team-reuse-results">
                        {rds.length === 0 && (
                          <div className="px-3 py-2 text-center text-[12px] text-muted-ink">Aucun résultat.</div>
                        )}
                        {rds.map((rd) => {
                          const already = sourceIds.has(rd.id) || extraIds.has(rd.id);
                          return (
                            <button key={rd.id} type="button" disabled={already} onClick={() => addExtra(rd)}
                              data-testid={`team-reuse-add-${rd.code}`}
                              className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors ${already ? "cursor-default opacity-50" : "hover:bg-[var(--surface-2)]"}`}>
                              <span className="min-w-0">
                                <span className="block break-words font-semibold text-ink">{rd.name}
                                  {rd.population === "independant" && <span className="ml-1 rounded bg-[rgba(93,63,211,0.12)] px-1 text-[9px] font-bold uppercase text-[#5D3FD3]">Indé</span>}</span>
                                {rd.code && <span className="font-mono text-[11px] font-bold text-[var(--shariti-orange)]">{rd.code}</span>}
                                {(() => {
                                  const dj = datedDispoEntry(rd.dispo_jour);
                                  const entry = dj || dispoForDate(rd.disponibilites, target?.date);
                                  if (!entry) return null;
                                  const appCd = (dj && dj.mode === "creneau") ? dj.debut : effCd;
                                  const appCf = (dj && dj.mode === "creneau") ? dj.fin : effCf;
                                  return (
                                    <span className="mt-0.5 block">
                                      <DispoMini entry={entry} creneau={{ debut: appCd, fin: appCf }} testId={`team-reuse-dispo-${rd.code}`} />
                                    </span>
                                  );
                                })()}
                              </span>
                              {already ? <Check className="h-4 w-4 text-[#0F5A2F]" /> : <Plus className="h-4 w-4 text-[var(--shariti-orange)]" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Appliquer aux jours</div>
                    {availableTargets.length > 0 && (
                      <button type="button" onClick={toggleAll} data-testid="team-reuse-toggle-all"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--shariti-orange)] hover:underline">
                        {allSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                        {allSelected ? "Tout désélectionner" : "Tout sélectionner"}
                      </button>
                    )}
                  </div>
                  {availableTargets.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-4 text-center text-[12px] text-muted-ink">
                      Aucun jour planifié pour cette ligne dans la semaine.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-1.5" data-testid="team-reuse-days">
                      {availableTargets.map((t) => {
                        const on = reuseSel.has(t.date);
                        return (
                          <button key={t.date} type="button" onClick={() => toggleDay(t.date)}
                            data-testid={`team-reuse-day-${t.date}`}
                            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-left text-[12px] font-semibold transition-colors ${on ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.10)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] text-ink hover:bg-[var(--surface-2)]"}`}>
                            {on ? <CheckSquare className="h-3.5 w-3.5 shrink-0" /> : <Square className="h-3.5 w-3.5 shrink-0" />}
                            <span className="inline-flex items-center gap-1"><CalendarRange className="h-3 w-3 shrink-0" />{frenchDayChip(String(t.date).slice(0, 10))}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <button type="button" onClick={confirmReuse}
                  disabled={reuseBusy || reuseSel.size === 0 || (includedCount + extraMembers.length) === 0}
                  data-testid="team-reuse-confirm"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--shariti-orange)] px-3 py-2.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50">
                  {reuseBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                  Reprendre {includedCount + extraMembers.length} pers. sur {reuseSel.size} jour(s)
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-5 p-4">
            {canEdit && (
            <>
            {/* Créneau à appliquer */}
            <div>
              <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Créneau à appliquer</div>
              {renderSlotPicker()}
            </div>

            {/* Recherche + ajout */}
            <div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Ajouter un collaborateur</div>
                <div className="flex items-center gap-1.5">
                  {!isPap && (
                    <button type="button" onClick={fillFromDispos} disabled={fillingDispos} data-testid="team-fill-dispos"
                      className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-soft)] px-2 py-1 text-[11px] font-semibold text-ink transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50"
                      title="Placer les collaborateurs disponibles ce jour (dispos déclarées par le responsable)">
                      {fillingDispos ? <Loader2 className="h-3 w-3 animate-spin text-[var(--shariti-orange)]" /> : <CalendarClock className="h-3 w-3 text-[var(--shariti-orange)]" />} Remplir avec les dispos
                    </button>
                  )}
                  {!isPap && (
                    <button type="button" onClick={redispatchDay} disabled={redispatching} data-testid="team-redispatch-day"
                      className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-soft)] px-2 py-1 text-[11px] font-semibold text-ink transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50"
                      title="Recalcule la répartition des collaborateurs disponibles sur les spots du jour (3 max par spot, équipe regroupée). Les placements manuels sont conservés.">
                      {redispatching ? <Loader2 className="h-3 w-3 animate-spin text-[var(--shariti-orange)]" /> : <Shuffle className="h-3 w-3 text-[var(--shariti-orange)]" />} Re-dispatcher la journée
                    </button>
                  )}
                  <button type="button" onClick={openReuse} data-testid="team-duplicate-yesterday"
                    className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-soft)] px-2 py-1 text-[11px] font-semibold text-ink transition-colors hover:bg-[var(--surface-2)]">
                    <Copy className="h-3 w-3 text-[var(--shariti-orange)]" /> Reprendre l'équipe précédente
                  </button>
                </div>
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
                <Input placeholder="Rechercher par nom ou code…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 pl-8" data-testid="team-search" />
              </div>
              <div className="mt-2 max-h-52 space-y-1 overflow-y-auto rounded-xl border border-[var(--border-soft)] p-1">
                {rds.length === 0 && (
                  <div className="px-3 py-3 text-center text-[12px] text-muted-ink">Aucun collaborateur actif terrain. Synchronisez PREREC (onglet Pointage & Heures).</div>
                )}
                {rds.map((rd) => {
                  const already = assignedIds.has(rd.id);
                  return (
                    <button key={rd.id} type="button" disabled={already || busyId === rd.id} onClick={() => assign(rd)}
                      data-testid={`team-add-${rd.code}`}
                      className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${already ? "cursor-default opacity-50" : "hover:bg-[var(--surface-2)]"}`}>
                      <span className="min-w-0">
                        <span className="block break-words font-semibold text-ink">{rd.name}
                          {rd.population === "independant" && <span className="ml-1 rounded bg-[rgba(93,63,211,0.12)] px-1 text-[9px] font-bold uppercase text-[#5D3FD3]" data-testid={`team-indep-badge-${rd.code}`}>Indé</span>}
                          {rd.is_demo && <span className="ml-1 rounded bg-[var(--surface-2)] px-1 text-[9px] uppercase text-muted-ink">démo</span>}</span>
                        {rd.code && <span className="font-mono text-[11px] font-bold text-[var(--shariti-orange)]">{rd.code}</span>}
                        {(() => {
                          const dj = datedDispoEntry(rd.dispo_jour);
                          const entry = dj || dispoForDate(rd.disponibilites, target?.date);
                          if (!entry) return null;
                          const appCd = (dj && dj.mode === "creneau") ? dj.debut : effCd;
                          const appCf = (dj && dj.mode === "creneau") ? dj.fin : effCf;
                          return (
                            <span className="mt-0.5 block">
                              <DispoMini entry={entry} creneau={{ debut: appCd, fin: appCf }} testId={`team-add-dispo-${rd.code}`} />
                            </span>
                          );
                        })()}
                      </span>
                      {busyId === rd.id ? <Loader2 className="h-4 w-4 animate-spin text-muted-ink" />
                        : already ? <Check className="h-4 w-4 text-[#0F5A2F]" />
                        : <Plus className="h-4 w-4 text-[var(--shariti-orange)]" />}
                    </button>
                  );
                })}
              </div>
            </div>
            </>
            )}

            {/* Équipe affectée */}
            <div>
              <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
                Équipe affectée{team.length ? ` (${team.length})` : ""}
              </div>
              {loading ? (
                <div className="rounded-xl border border-[var(--border-soft)] px-3 py-4 text-center text-[12px] text-muted-ink">Chargement…</div>
              ) : team.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-5 text-center text-[12px] text-muted-ink" data-testid="team-empty">
                  {canEdit ? "Aucun collaborateur affecté. Ajoutez-en un ci-dessus (moins de 30 secondes)." : "Aucun collaborateur affecté pour le moment."}
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="team-list">
                  {team.map((m) => {
                    const needsCancel = m.has_pointage_data && !m.annule && m.pointage_id;
                    return (
                    <div key={m.affectation_id} className="rounded-xl border border-[var(--border-soft)] bg-white px-3 py-2" data-testid={`team-member-${m.code_rd}`}>
                      <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="break-words text-sm font-semibold text-ink">{m.rd_name}
                          {m.population === "independant" && <span className="ml-1 rounded bg-[rgba(93,63,211,0.12)] px-1 text-[9px] font-bold uppercase text-[#5D3FD3]" data-testid={`team-member-indep-${m.code_rd}`}>Indé</span>}
                          {m.dispatched_by === "resp" && <span className="ml-1 rounded bg-[rgba(238,114,46,0.14)] px-1 text-[9px] font-bold uppercase text-[#9A4412]" title={`Placé par le responsable${m.dispatched_by_chef_name ? ` ${m.dispatched_by_chef_name}` : ""}`} data-testid={`team-member-resp-${m.code_rd}`}>Responsable</span>}
                          {m.dispatched_by !== "resp" && m.auto_dispo && <span className="ml-1 rounded bg-[var(--surface-2)] px-1 text-[9px] font-bold uppercase text-muted-ink" title="Placement automatique (dispos → planning)" data-testid={`team-member-auto-${m.code_rd}`}>Auto</span>}</div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-ink">
                          {m.code_rd && <span className="font-mono font-bold text-[var(--shariti-orange)]">{m.code_rd}</span>}
                          <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" />{m.creneau_debut}–{m.creneau_fin}</span>
                          {/* État du pointage : neutralisé (0 h), heures pointées, ou aucun pointage. */}
                          {m.annule ? (
                            <span className="inline-flex items-center gap-0.5 rounded bg-[var(--surface-2)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-muted-ink" data-testid={`team-member-annule-${m.code_rd}`}>
                              <Ban className="h-2.5 w-2.5" /> Annulé · 0 h
                            </span>
                          ) : m.has_pointage_data ? (
                            <span className="inline-flex items-center gap-0.5 rounded bg-[rgba(31,157,85,0.12)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#0F5A2F]" data-testid={`team-member-pointe-${m.code_rd}`}>
                              Pointé{m.heures_retenues != null ? ` · ${Number(m.heures_retenues).toFixed(2).replace(".", ",")} h` : ""}
                            </span>
                          ) : null}
                        </div>
                        {(() => {
                          const entry = datedDispoEntry(m.dispo_jour) || dispoForDate(m.disponibilites, target?.date);
                          if (!entry) return null;
                          return (
                            <div className="mt-0.5">
                              <DispoMini entry={entry} creneau={{ debut: m.creneau_debut, fin: m.creneau_fin }} testId={`team-member-dispo-${m.code_rd}`} />
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {/* Contact direct du collaborateur (numéro issu de la fiche RD / PREREC). */}
                        {m.phone ? (
                          <>
                            <a href={`tel:${m.phone}`} title={`Appeler ${m.rd_name} · ${m.phone}`}
                              data-testid={`team-call-${m.code_rd}`}
                              className="grid h-8 w-8 place-items-center rounded-lg bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)] transition-colors hover:bg-[rgba(238,114,46,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]/40">
                              <Phone className="h-4 w-4" />
                            </a>
                            {waHref(m.phone) && (
                              <a href={waHref(m.phone)} target="_blank" rel="noreferrer"
                                title={`WhatsApp ${m.rd_name} · ${m.phone}`} data-testid={`team-wa-${m.code_rd}`}
                                className="grid h-8 w-8 place-items-center rounded-lg bg-[rgba(31,157,85,0.12)] text-[#0F5A2F] transition-colors hover:bg-[rgba(31,157,85,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F9D55]/40">
                                <MessageCircle className="h-4 w-4" />
                              </a>
                            )}
                          </>
                        ) : (
                          <span data-testid={`team-nophone-${m.code_rd}`}
                            title="Aucun numéro — non renseigné dans PREREC ou fiche RD à compléter"
                            className="px-1 text-[9px] font-semibold uppercase tracking-wide text-muted-ink">N° —</span>
                        )}
                        {canEdit && !m.annule && (
                          <button type="button" onClick={() => (editId === m.affectation_id ? cancelEdit() : startEdit(m))}
                            data-testid={`team-edit-open-${m.code_rd}`}
                            title="Modifier le créneau (sans le supprimer)"
                            className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors ${editId === m.affectation_id ? "bg-[var(--shariti-orange)] text-white" : "text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.12)]"}`}>
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        {canEdit && (
                        <button type="button" onClick={() => remove(m)} disabled={busyId === m.affectation_id}
                          data-testid={`team-remove-${m.code_rd}`}
                          title={needsCancel ? "Annuler le pointage (met les heures à 0)" : "Retirer de l'équipe"}
                          className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors disabled:opacity-50 ${needsCancel ? "text-[#8a6600] hover:bg-[rgba(185,137,0,0.12)]" : "text-[#9B2226] hover:bg-[rgba(214,69,69,0.10)]"}`}>
                          {busyId === m.affectation_id ? <Loader2 className="h-4 w-4 animate-spin" /> : needsCancel ? <Ban className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                        </button>
                        )}
                      </div>
                      </div>
                      {editId === m.affectation_id && (
                        <div className="mt-2 space-y-2 rounded-lg border border-[var(--shariti-orange)]/40 bg-[rgba(238,114,46,0.06)] p-2" data-testid={`team-edit-${m.code_rd}`}>
                          <div className="text-[10px] font-bold uppercase tracking-wide text-muted-ink">Modifier le créneau</div>
                          <div className="flex flex-wrap gap-1">
                            {TEAM_SLOTS.map((s) => (
                              <button key={s.label} type="button" onClick={() => { setEDeb(s.cd); setEFin(s.cf); }}
                                className={`rounded-md px-2 py-1 text-[10px] font-bold transition-colors ${eDeb === s.cd && eFin === s.cf ? "bg-[var(--shariti-orange)] text-white" : "bg-white text-muted-ink hover:bg-[var(--surface-2)]"}`}
                                data-testid={`team-edit-slot-${m.code_rd}-${s.cd}`}>{s.label}</button>
                            ))}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Input type="time" value={eDeb} onChange={(e) => setEDeb(e.target.value)} className="h-8 w-[112px] bg-white" data-testid={`team-edit-debut-${m.code_rd}`} />
                            <span className="text-xs text-muted-ink">→</span>
                            <Input type="time" value={eFin} onChange={(e) => setEFin(e.target.value)} className="h-8 w-[112px] bg-white" data-testid={`team-edit-fin-${m.code_rd}`} />
                            <button type="button" onClick={() => saveEdit(m)} disabled={savingEdit}
                              className="ml-auto inline-flex h-8 items-center gap-1 rounded-lg bg-[var(--shariti-orange)] px-3 text-[12px] font-bold text-white transition-colors hover:bg-[#E56622] disabled:opacity-50"
                              data-testid={`team-edit-save-${m.code_rd}`}>
                              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-3.5 w-3.5" /> Enregistrer</>}
                            </button>
                            <button type="button" onClick={cancelEdit}
                              className="grid h-8 w-8 place-items-center rounded-lg text-muted-ink transition-colors hover:bg-[var(--surface-2)]"
                              data-testid={`team-edit-cancel-${m.code_rd}`}>
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          {creneauOutsideDispo(dispoForDate(m.disponibilites, target?.date), eDeb, eFin) && (
                            <div className="inline-flex items-center gap-1 rounded bg-[rgba(214,69,69,0.10)] px-2 py-1 text-[10px] font-semibold text-[#9B2226]">
                              <AlertTriangle className="h-3 w-3" /> Ce créneau est hors de sa disponibilité déclarée
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>

      {/* ===== Dialog : ligne TRACÉE (annulation 0 h + option admin de suppression) ===== */}
      <AlertDialog open={!!annulerTarget} onOpenChange={(o) => { if (!o && !annulerBusy) setAnnulerTarget(null); }}>
        <AlertDialogContent data-testid="team-annuler-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-ink">
              <Ban className="h-5 w-5 text-[#8a6600]" /> {annulerTarget?.annule ? "Ligne tracée (audit)" : "Annuler le pointage"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-ink">
              {annulerTarget?.annule ? (
                <>Le pointage de <b>{annulerTarget?.rd_name}</b> est déjà annulé (0 h) et conservé pour l'audit.
                {isAdmin ? " En tant qu'admin, vous pouvez le supprimer définitivement (ex. pour déplacer le spot sur une autre ligne)." : " Il ne peut pas être supprimé physiquement."}</>
              ) : (
                <>La suppression physique est bloquée car <b>{annulerTarget?.rd_name}</b> a un pointage
                sur cette journée (règle d'audit). Annuler le pointage met ses <b>heures à 0</b>
                {annulerTarget?.heures_retenues != null ? ` (actuellement ${Number(annulerTarget.heures_retenues).toFixed(2).replace(".", ",")} h)` : ""} et
                corrige tout <b>double comptage</b>, tout en conservant la trace pour l'audit.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!annulerTarget?.annule && (
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Motif (obligatoire)</label>
              <Textarea
                value={annulerMotif}
                onChange={(e) => setAnnulerMotif(e.target.value)}
                placeholder="Ex. : affectation en doublon / erreur de saisie"
                rows={3}
                data-testid="team-annuler-motif"
                className="resize-none"
              />
            </div>
          )}
          {isAdmin && (
            <div className="mt-1 rounded-lg border border-[rgba(155,34,38,0.35)] bg-[rgba(155,34,38,0.05)] p-2.5">
              <p className="text-[11px] font-semibold text-[#9B2226]">Contrôle admin — suppression définitive</p>
              <p className="mt-0.5 text-[11px] text-muted-ink">Retire la ligne et son pointage (utile pour déplacer un spot mal attribué). Le geste est tracé dans le journal.</p>
              <button
                type="button"
                onClick={() => forceDelete()}
                disabled={annulerBusy}
                data-testid="team-force-delete"
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#9B2226] px-3 py-1.5 text-[12px] font-bold text-white transition-colors hover:bg-[#83191d] disabled:opacity-50"
              >
                {annulerBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Supprimer définitivement
              </button>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={annulerBusy} data-testid="team-annuler-cancel">Fermer</AlertDialogCancel>
            {!annulerTarget?.annule && (
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); confirmAnnuler(); }}
                disabled={annulerBusy || !annulerMotif.trim()}
                data-testid="team-annuler-confirm"
                className="bg-[#8a6600] text-white hover:bg-[#725500]"
              >
                {annulerBusy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Annulation…</> : "Annuler le pointage (0 h)"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
